import { useCallback } from 'react';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { StepValidation } from '../../types/onboarding';

export const useOnboardingFlow = () => {
  const {
    currentStep,
    setCurrentStep,
    completedSteps,
    markStepComplete,
    canNavigateToStep,
    userData,
    isSubmitting,
    error,
    setError,
  } = useOnboardingStore();

  const goToStep = useCallback((step: number) => {
    if (canNavigateToStep(step)) {
      setCurrentStep(step);
    }
  }, [canNavigateToStep, setCurrentStep]);

  const goNext = useCallback(() => {
    const nextStepNumber = currentStep + 1;
    if (nextStepNumber <= 6) {
      markStepComplete(currentStep);
      setCurrentStep(nextStepNumber);
    }
  }, [currentStep, markStepComplete, setCurrentStep]);

  const goPrevious = useCallback(() => {
    const previousStepNumber = currentStep - 1;
    if (previousStepNumber >= 1) {
      setCurrentStep(previousStepNumber);
    }
  }, [currentStep, setCurrentStep]);

  const skipStep = useCallback(() => {
    // Mark current step as completed even though it was skipped
    markStepComplete(currentStep);
    goNext();
  }, [currentStep, markStepComplete, goNext]);

  const validateStep = useCallback((stepNumber: number): StepValidation => {
    switch (stepNumber) {
      case 1: // Welcome - no validation needed
        return { isValid: true };
        
      case 2: // Basic Info
        const hasRequiredBasicInfo = 
          userData.firstName.trim() !== '' &&
          userData.lastName.trim() !== '' &&
          userData.birthday !== null;
        return {
          isValid: hasRequiredBasicInfo,
          errors: hasRequiredBasicInfo ? [] : ['Please fill in all required fields'],
        };
        
      case 3: // Avatar
        return {
          isValid: userData.learningAvatar !== null,
          errors: userData.learningAvatar ? [] : ['Please select a learning avatar'],
        };
        
      case 4: // Academic
        const hasRequiredAcademic = 
          userData.school.trim() !== '' &&
          userData.grade.trim() !== '';
        return {
          isValid: hasRequiredAcademic,
          errors: hasRequiredAcademic ? [] : ['Please fill in school and grade information'],
        };
        
      case 5: // Courses
        const hasSelectedCourses = 
          userData.favoriteCourses.length > 0 &&
          userData.challengingCourses.length > 0;
        return {
          isValid: hasSelectedCourses,
          errors: hasSelectedCourses ? [] : ['Please add at least one favorite course and one challenging course'],
        };
        
      default:
        return { isValid: true };
    }
  }, [userData]);

  const canProceed = useCallback((): boolean => {
    const validation = validateStep(currentStep);
    return validation.isValid;
  }, [currentStep, validateStep]);

  return {
    currentStep,
    totalSteps: 5, // Total steps excluding completion
    completedSteps,
    goNext,
    goPrevious,
    goToStep,
    skipStep,
    canProceed,
    validateStep,
    isFirstStep: currentStep === 1,
    isLastStep: currentStep === 5, // Courses is the last step before completion
    userData,
    isSubmitting,
    error,
    setError,
  };
};